import type { ReactNode } from 'react'
import { cx } from './cx'
import type { SortDir } from '../../lib/list-state'

/**
 * A table header you can sort by. Extracted from UsersPage; Business and
 * Infrastructure were hand-writing `onClick={() => toggleSort(k)}` plus a text
 * arrow on every `<th>`.
 *
 * Pair it with `useListState`, which owns `sortKey`/`sortDir`/`toggleSort`.
 */
export default function SortableTh<K extends string>({
  label,
  sortKey,
  current,
  dir,
  onSort,
  align,
  className,
}: {
  label: ReactNode
  /** The column this header sorts by. */
  sortKey: K
  /** The column currently sorted on. */
  current: K
  dir: SortDir
  onSort: (key: K) => void
  /** Mirrors the chevron to the left so it hugs a right-aligned number. */
  align?: 'right'
  className?: string
}) {
  const active = current === sortKey
  return (
    <th className={cx('font-medium', className)} aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cx(
          'inline-flex items-center gap-1 transition-colors hover:text-gray-900',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-sm',
          active && 'text-gray-900',
          align === 'right' && 'flex-row-reverse',
        )}
      >
        {label}
        <svg
          className={cx('w-3 h-3', active ? 'opacity-100' : 'opacity-30')}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          {active && dir === 'asc' ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          )}
        </svg>
      </button>
    </th>
  )
}
